import { useState } from 'react';
import { Loader2, Search, AlertTriangle, CheckCircle2, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmt(v) {
  if (v == null) return '--';
  const n = Number(v);
  return isNaN(n) ? String(v) : n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function Stat({ label, value, sub, warn }) {
  return (
    <div className={`rounded-lg border p-3 ${warn ? 'border-red-300 dark:border-red-800' : ''}`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-xl font-bold tabular-nums ${warn ? 'text-red-600 dark:text-red-400' : ''}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

export function UplistingCancellationFeeExposurePanel() {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const runCheck = async () => {
    setLoading(true);
    setResults(null);
    try {
      const { data } = await diagnosticsAPI.uplistingCancellationFeeExposure();
      setResults(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const verdictColor = results?.verdict === 'safe_to_apply'
    ? 'green' : results?.verdict === 'trivial' ? 'amber' : 'red';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-orange-500" />
          Uplisting Cancellation Fee Exposure
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Sizes the cancellation-fee revenue across the cancelled records the
          backfill flagged. Confirms whether Apply would destroy real revenue
          or correctly zero free cancellations.
        </p>

        <Button onClick={runCheck} disabled={loading}>
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Search className="h-4 w-4 mr-2" />
          )}
          Run Check
        </Button>

        {results && (
          <div className="space-y-4">
            {/* Verdict banner */}
            {verdictColor === 'green' && (
              <div className="flex items-start gap-3 rounded-lg border p-4 bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800">
                <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                <div className="space-y-1 text-sm">
                  <p className="font-medium text-green-700 dark:text-green-400">Safe to Apply</p>
                  <p className="text-muted-foreground">{results.verdict_detail}</p>
                </div>
              </div>
            )}
            {verdictColor === 'amber' && (
              <div className="flex items-start gap-3 rounded-lg border p-4 bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800">
                <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />
                <div className="space-y-1 text-sm">
                  <p className="font-medium text-amber-700 dark:text-amber-400">Trivial Revenue</p>
                  <p className="text-muted-foreground">{results.verdict_detail}</p>
                </div>
              </div>
            )}
            {verdictColor === 'red' && (
              <div className="flex items-start gap-3 rounded-lg border p-4 bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800">
                <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />
                <div className="space-y-1 text-sm">
                  <p className="font-medium text-red-700 dark:text-red-400">Do Not Apply</p>
                  <p className="text-muted-foreground">{results.verdict_detail}</p>
                </div>
              </div>
            )}

            {/* Rollup stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Total cancelled (non-zero)" value={results.total_records} />
              <Stat
                label="With cancellation fee"
                value={results.with_cancellation_fee_gt_zero}
                warn={results.with_cancellation_fee_gt_zero > 0}
              />
              <Stat
                label="Total cancellation fee"
                value={`\u20AC${fmt(results.total_cancellation_fee_eur)}`}
                warn={parseFloat(results.total_cancellation_fee_eur) > 0}
              />
              <Stat
                label="Est. owner revenue"
                value={`\u20AC${fmt(results.estimated_owner_cancellation_revenue_eur)}`}
                warn={parseFloat(results.estimated_owner_cancellation_revenue_eur) > 0}
              />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Stat
                label="Free cancellations (fee=0)"
                value={results.with_cancellation_fee_zero_or_absent}
              />
              <Stat
                label="Managed properties"
                value={results.managed_property_records}
                sub="100% to Fan regardless"
              />
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Payload carries own commission?</p>
                <Badge variant={results.cancellation_payload_carries_own_commission ? 'default' : 'secondary'} className="mt-1">
                  {results.cancellation_payload_carries_own_commission
                    ? `yes (${results.commission_field_in_payload})`
                    : 'no (derived from booking rate)'}
                </Badge>
              </div>
            </div>

            {/* Top records table */}
            {results.top_records?.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold">
                  Top {results.top_records.length} by cancellation fee
                </h3>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-2 font-medium">Uplisting ID</th>
                        <th className="text-left px-3 py-2 font-medium">Guest</th>
                        <th className="text-left px-3 py-2 font-medium">Property</th>
                        <th className="text-left px-3 py-2 font-medium">Channel</th>
                        <th className="text-right px-3 py-2 font-medium">Cancel Fee</th>
                        <th className="text-left px-3 py-2 font-medium">Source</th>
                        <th className="text-right px-3 py-2 font-medium">Owner Rev</th>
                        <th className="text-left px-3 py-2 font-medium">Mgmt</th>
                        <th className="text-right px-3 py-2 font-medium">Comm. Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.top_records.map((r, i) => (
                        <tr key={r.uplisting_id || i} className="border-b last:border-0">
                          <td className="px-3 py-2 font-mono text-xs">{r.uplisting_id}</td>
                          <td className="px-3 py-2 text-xs">{r.guest_name}</td>
                          <td className="px-3 py-2 text-xs">{r.property_name}</td>
                          <td className="px-3 py-2 text-xs">{r.channel}</td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums">
                            {fmt(r.cancel_fee_value)}
                          </td>
                          <td className="px-3 py-2 text-xs font-mono">{r.cancel_fee_source}</td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums">
                            {fmt(r.derived_owner_revenue)}
                          </td>
                          <td className="px-3 py-2 text-xs">
                            <Badge variant={r.management_type === 'managed' ? 'default' : 'outline'}>
                              {r.management_type}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-xs">
                            {r.commission_rate_from_booking != null
                              ? `${(r.commission_rate_from_booking * 100).toFixed(1)}%`
                              : '--'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Raw payload sample for the top record */}
            {results.top_records?.[0]?.raw_payload_monetary &&
              Object.keys(results.top_records[0].raw_payload_monetary).length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold">
                  Raw payload monetary fields (top record: {results.top_records[0].uplisting_id})
                </h3>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-2 font-medium">Field</th>
                        <th className="text-right px-3 py-2 font-medium">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(results.top_records[0].raw_payload_monetary).map(([k, v]) => (
                        <tr key={k} className="border-b last:border-0">
                          <td className="px-3 py-2 font-mono text-xs">{k}</td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums">{fmt(v)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
